import angular from 'angular';
import uiRouter from '@uirouter/angularjs';
import 'oclazyload';
import { UiRouterRun } from './core/routes/ui-router-run';

export const PublicAngularJSRoutingModule = angular.module('buildium-public.routing', [
    uiRouter,
    'oc.lazyLoad'
])
    .config(['$locationProvider', '$uiRouterProvider', ($locationProvider, $uiRouterProvider) => {
        $locationProvider.html5Mode(true);

        const { stateRegistry } = $uiRouterProvider;

        /**
         * HYBRID ROUTING
         *
         * In hybrid routing, the Angular router is the primary url matcher. When an angularjs url is matched,
         * ui-router starts matching urls with registered states. The sink state is used to handle unmatched urls
         * and transfer routing back to Angular router.
         * https://blog.nrwl.io/using-ngupgrade-like-a-pro-lazy-loading-angularjs-applications-469819f5c86
         */
        const sinkState = {
            name: 'sink',
            url: '/*path',
            template: ''
        };
        stateRegistry.register(sinkState);

        /**
         * ANGULARJS LAZY-LOADING
         *
         * This is used to generate ui-router future states and to create separate bundles for each feature module.
         * Note: For lazy-loading to work, the feature module must not be ES6 imported in any other angularjs module.
         * createFutureState() does the magic via dynamic import() and ocLazyLoad().
         */
        // TODO - move apps to 'ajs-app/features' and restore './features' import path.
        function createFutureState(module) {
            // toplevelState, dir and filename normally share the same name by convention
            let importDir = `${module.topLevelState}`;
            let importFilenameSegment = `${module.topLevelState}`;

            // TODO - some feature modules have not been refactored, due to impact and risk associated with URL changes (H2-3550)
            if (module.modulePath != null) {
                // eslint-disable-next-line prefer-destructuring
                importDir = module.modulePath.split('/')[1];
                // trim the ".module" part, since webpack can't handle it
                importFilenameSegment = module.modulePath.split('/')[2].slice(0, -7);
            }

            const futureState = {
                name: `${module.topLevelState}.**`,
                url: module.topLevelUrl,
                lazyLoad($transitions$) {
                    const ocLazyLoad = $transitions$.injector().get('$ocLazyLoad');

                    // https://webpack.js.org/api/module-methods/#dynamic-expressions-in-import
                    // When webpack gets to this dynamic import, it will cause every .module file
                    // in the ../../../../public directory to be bundled into a new chunk.

                    // It is not possible to use a fully dynamic import statement, such as import(foo).
                    // Because foo could potentially be any path to any file in your system or project.
                    // The import() must contain at least some information about where the module is located.

                    // WARNING - due to the nature of webpack dynamic import bundling, variable replacement
                    //           in the import path should be limited to simple strings (no special characters)

                    return import(
                        `../../../../public/${importDir}/${importFilenameSegment}.module`).then(mod => {
                        return ocLazyLoad.inject(mod[module.moduleName]);
                    });
                }
            };
            return futureState;
        }

        function registerFutureStates(modules) {
            const lazyModules = modules.filter(mod => mod.isLazyLoaded);
            lazyModules.forEach(mod => {
                const futureState = createFutureState(mod);
                stateRegistry.register(futureState);
            });
        }

        registerFutureStates(angularJsFeatureModules);
    }])
    .run(UiRouterRun)
    .name;

/**
 * `moduleName`: Use the exported name from the feature module file.
 *
 * `modulePath`: (OPTIONAL) Use this if the topLevelState differs from the path and filename convention.
 *               This must be in the format '/subdir/filename.module' relative to the /app folder.
 *               e.g: '/rent-roll/rent-roll.module'
 *
 * `topLevelState`: This must match the top-level state in the routes file of the feature module.
 *
 * `topLevelUrl`: This must match the top-level url segment in the routes file of the feature module.
 *
 * `isLazyLoaded`:  true - this will register the module as a future state.
 *                 false - useful as a temporary workaround if tracking down dependencies is difficult.
 *                 Note: if false, the module will need to be imported in the main AngularJSModule
 */
export const angularJsFeatureModules = [
    {
        moduleName: 'PublicAuthenticationApp',
        topLevelState: 'authentication',
        topLevelUrl: '/authentication',
        isLazyLoaded: true
    },
    {
        moduleName: 'SetNewPasswordApp',
        topLevelState: 'set-new-password',
        topLevelUrl: '/set-new-password',
        isLazyLoaded: true
    },
    {
        moduleName: 'SetupTwoFactorAuthenticationApp',
        topLevelState: 'set-up-two-factor-authentication',
        topLevelUrl: '/set-up-two-factor-authentication',
        isLazyLoaded: true
    },
    {
        moduleName: 'UserAgreementsApp',
        modulePath: '/user-agreements/user-agreements.module',
        topLevelState: 'userAgreements',
        topLevelUrl: '/user-agreements',
        isLazyLoaded: true
    }
];

/**
 * HYBRID ROUTING
 * All angularjs top-level routes are exported here, so that Angular router can transfer routing to angularjs and ui-router.
 */
export const defaultAngularJsRoutes = [];

angularJsFeatureModules.forEach(mod => {
    const routeSegment = mod.topLevelUrl.slice(mod.topLevelUrl.indexOf('/') + 1);
    defaultAngularJsRoutes.push(routeSegment);
});
